import React from 'react';
import { Layout, Row, Col, Card, Button, message, Alert } from 'antd';
import { EditFilled, UploadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	siteSurveysRestCrudThunksForBuyer,
	siteSurveysRestCrudThunksForSupplier,
} from '../../thunks/site_surveys_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import LocationCardDisplay from '../location_card_display/LocationCardDisplay';
import TaskListRO from '../task_list/TaskListRO';
import ScheduleWalkthroughModalForm from '../schedule_walkthrough_modal_form/ScheduleWalkthroughModalForm';
import LogOnMountWithStandardEventProperties from '../log_on_mount_with_standard_event_properties/LogOnMountWithStandardEventProperties';
import { ExportToCsv } from 'export-to-csv';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import SiteSurveyWalkthroughSection from './SiteSurveyWalkthroughSection';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import FlatfileImporter from '../flatfile/FlatfileImporter';
import {
	REGEX_CONFIG_NAMES,
	regexRecordHook,
	requiredWithValueRecordHook,
} from '../flatfile/faltfile_utils';
import {
	getMultipleChoiseWithLabelForBE,
	getMultipleChoiseWithLabelForExport,
	validateForDuplicateTitlesUnderSameGroup,
} from '../../utils/SiteSurveyUtils';
import WalkthroughHeatmap from '../WalkthroughHeatmap';
import moment from 'moment';
import QRCode from 'qrcode.react';
import QRCodePrinter from '../PrintableQrCode';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';

const { Content } = Layout;

const csvOptions = {
	fieldSeparator: ',',
	quoteStrings: '"',
	decimalSeparator: '.',
	showLabels: true,
	showTitle: false,
	title: 'Task List',
	useTextFile: false,
	useBom: true,
	headers: ['Title', 'Description', 'Group', 'Question Type', 'Mutliple Choices'],
};

const FLATFILE_FIELDS = [
	{
		label: 'Title',
		key: 'title',
		type: 'string',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Description',
		key: 'description',
		type: 'string',
	},
	{
		label: 'Group',
		key: 'groupTitle',
		type: 'string',
	},
	{
		label: 'Question Type',
		key: 'questionType',
		type: 'enum',
		config: {
			options: [
				{ label: 'Multiple Choice', value: 'multipleChoice' },
				{ label: 'Free Text', value: 'textbox' },
			],
		},
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Answer Options',
		key: 'multipleChoicesWithLabel',
		type: 'string',
	},
];

class SiteSurveysDetailPage extends React.Component<any, any> {
	formRefs: any = {
		scheduleWalkthroughForm: null,
	};

	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			scheduleWalkthroughFormVisible: false,
			uploadTaskModalVisible: false,
			editTaskListVisible: false,
		};
	}

	componentDidMount() {
		const { getSiteSurvey, match } = this.props;
		const siteSurveyId = match.params.id;
		getSiteSurvey(siteSurveyId).then(() => {
			this.setState({ loading: false });
		});
	}

	handleSchedule = (e) => {
		this.setState({
			scheduleWalkthroughFormVisible: true,
		});
		e.stopPropagation();
		e.preventDefault();
	};

	refreshSiteSurvey = () => {
		const { getSiteSurvey, match } = this.props;
		this.setState({ loading: true });
		const siteSurveyId = match.params.id;
		getSiteSurvey(siteSurveyId).then(() => {
			this.setState({ loading: false });
		});
	};

	handleScheduleWalkthroughCancel = () => {
		this.setState({
			scheduleWalkthroughFormVisible: false,
		});
	};

	saveFormRef = (formName) => (formRef) => {
		this.formRefs[formName] = formRef;
	};

	onUploadTaskList = () => {
		this.setState({ uploadTaskModalVisible: true });
	};

	onUploadTaskModalClose = () => {
		this.setState({ uploadTaskModalVisible: false });
	};

	handleScheduleSuccess = () => {
		message.success('Walkthrough scheduled successfully!');
		this.refreshSiteSurvey();
		this.handleScheduleWalkthroughCancel();
	};

	showEditTaskList = () => this.setState({ editTaskListVisible: true });
	_onEditTaskListComplete = () => this.setState({ editTaskListVisible: false });

	getSourceDataForSiteSurvey = (ss) => {
		const tasks = nullSafeGetOrElse('tasks', ss, []);
		const formattedTasks = tasks.map((task) => ({
			...task,
			...(task.multipleChoicesWithLabel && {
				multipleChoicesWithLabel: getMultipleChoiseWithLabelForExport(
					task.multipleChoicesWithLabel
				),
			}),
		}));
		return formattedTasks;
	};

	onDownloadTaskList = () => {
		const { siteSurvey } = this.props;
		const tasks = nullSafeGetOrElse('tasks', siteSurvey, []);
		const formattedTasks = tasks.map((task) => ({
			title: task.title,
			description: task.description || '',
			group: task.groupTitle || '',
			questionType: task.questionType,
			multipleChoices: task.multipleChoicesWithLabel
				? getMultipleChoiseWithLabelForExport(task.multipleChoicesWithLabel)
				: '',
		}));
		const csvExporter = new ExportToCsv({ ...csvOptions, filename: siteSurvey.title });
		csvExporter.generateCsv(formattedTasks);
	};

	onDataLoad = ({ validData }) => {
		return new Promise((resolve, reject) => {
			const { siteSurvey, updateSiteSurvey } = this.props;
			const newSiteSurvey = {
				...siteSurvey,
				areTasksEdited: true,
				tasks: validData.map((task, orderingId) => ({
					...task,
					orderingId,
					...(task.multipleChoicesWithLabel && {
						multipleChoicesWithLabel: getMultipleChoiseWithLabelForBE(
							task.multipleChoicesWithLabel
						),
					}),
				})),
			};
			updateSiteSurvey(newSiteSurvey)
				.then(() => {
					resolve('Updated successfully!');
					this.refreshSiteSurvey();
				})
				.catch(reject);
		});
	};

	recordHook = (record) => {
		const updatedRecord = regexRecordHook([REGEX_CONFIG_NAMES.MULTIPLE_CHOICE_VALUES])(record);
		return requiredWithValueRecordHook(['multipleChoicesWithLabel'], 'questionType', [
			'multipleChoice',
		])(updatedRecord);
	};

	render() {
		const { siteSurvey, match, userType } = this.props;

		const { loading } = this.state;

		const siteSurveyId = match.params.id;

		return loading ? (
			<PageLoadingPlaceholder />
		) : (
			<Content className="siteSurveysDetailDetailsPage">
				<LogOnMountWithStandardEventProperties
					eventType={`visited ${userType} site survey detail page`}
				/>
				{this.state.scheduleWalkthroughFormVisible ? (
					<ScheduleWalkthroughModalForm
						onCancel={this.handleScheduleWalkthroughCancel}
						onSuccess={this.handleScheduleSuccess}
						siteSurveyId={siteSurveyId}
					/>
				) : null}
				<Row style={{ margin: '16px 4px' }} gutter={12}>
					<Col span={16}>
						<WalkthroughHeatmap
							siteSurveyId={match.params.id}
							startDate={moment().subtract(6, 'months')}
							endDate={moment()}
						/>
					</Col>
					<Col span={8}>
						{nullSafeGet('siteSurveyUUID', siteSurvey) ? (
							<div className="rowSpacing h-full">
								<QRCodePrinter
									value={siteSurvey.siteSurveyUUID}
									title={'Scan QR code to begin walkthrough'}
									description={`${siteSurvey.title} at ${nullSafeGet('location.name', siteSurvey)}`}
									cardTitle={'Site Survey Label'}
								>
									<Alert
										style={{ marginTop: '32px' }}
										message={
											<div className="text-sm">
												Scan this QR code from the walkthroughs section in the OpenWrench app to
												access the site survey on your mobile device.
											</div>
										}
										showIcon
										type="info"
									/>
								</QRCodePrinter>
							</div>
						) : nullSafeGet('location', siteSurvey) ? (
							<LocationCardDisplay location={siteSurvey.location} />
						) : null}
					</Col>
				</Row>
				<Row style={{ margin: '16px 4px' }} gutter={12}>
					<Col span={24}>
						<div className="rowSpacing">
							<Card
								title="Walkthroughs"
								extra={
									<AccessPermissionChecker name={PERMISSION_NAMES.ADD_WALKTHROUGHS}>
										<Button
											style={{
												position: 'absolute',
												right: 32,
												top: 12.5,
											}}
											type="primary"
											onClick={this.handleSchedule}
										>
											Schedule Walkthrough
										</Button>
									</AccessPermissionChecker>
								}
							>
								<SiteSurveyWalkthroughSection />
							</Card>
						</div>
						<div className="rowSpacing">
							<Card
								title="Tasks"
								extra={
									<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEYS}>
										<div>
											<Button onClick={this.showEditTaskList}>
												<EditFilled /> Edit Task List
											</Button>
											{this.state.editTaskListVisible ? (
												<FlatfileImporter
													dataLoadComplete={this.state.editTaskListVisible}
													type="site_survey"
													fields={FLATFILE_FIELDS}
													data={this.getSourceDataForSiteSurvey(siteSurvey)}
													onComplete={this._onEditTaskListComplete}
													onDataLoad={this.onDataLoad}
													recordHook={this.recordHook}
													validateRecordsOnSubmit={validateForDuplicateTitlesUnderSameGroup}
												/>
											) : null}
											<Button
												type="primary"
												style={{ marginLeft: '24px' }}
												onClick={this.onUploadTaskList}
											>
												<UploadOutlined />
												Replace Task List
											</Button>
										</div>
									</AccessPermissionChecker>
								}
							>
								<TaskListRO tasks={nullSafeGetOrElse('tasks', siteSurvey, [])} />
							</Card>
						</div>
					</Col>
				</Row>
				{this.state.uploadTaskModalVisible ? (
					<UploadWithSampleModal
						type="site_survey_upload"
						entityName="task"
						title="Replace Task List"
						downloadText="You can choose to download the existing list, add new tasks and upload! Do you want to download?"
						sampleFileText="Download task list"
						onCancel={this.onUploadTaskModalClose}
						onDownloadSample={this.onDownloadTaskList}
						fields={FLATFILE_FIELDS}
						onDataLoad={this.onDataLoad}
						showWarningMessage
						recordHook={this.recordHook}
						validateRecordsOnSubmit={validateForDuplicateTitlesUnderSameGroup}
					/>
				) : null}
			</Content>
		);
	}
}

const mapStateToProps = (state) => ({
	siteSurveysFetching: state.site_surveys.fetching,
	siteSurvey: state.site_surveys.detail,
	walkthroughs: state.walkthroughs,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getSiteSurvey: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveysRestCrudThunksForSupplier.readOne(id)
				: siteSurveysRestCrudThunksForBuyer.readOne(id)
		),
	updateSiteSurvey: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveysRestCrudThunksForSupplier.update(id)
				: siteSurveysRestCrudThunksForBuyer.update(id)
		),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(SiteSurveysDetailPage as any) as any)
);
